import * as React from 'react'

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  MoveHorizontalIcon,
  PrinterIcon,
  ScanIcon,
  VideoIcon,
  XIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'

import { DeliveryStatus } from '~/client/graph'
import { Button } from '~/client/src/shared/components/Button/Button'
import * as Icons from '~/client/src/shared/components/Icons'
import SwipeableWrapper from '~/client/src/shared/components/SwipeableWrapper/SwipeableWrapper'
import FileType from '~/client/src/shared/enums/FileType'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { formatStatusToDisplay } from '../../constants/DeliveryStatus'
import Keys from '../../enums/Keys'
import IFilePreviewProperties from '../../interfaces/IFilePreviewProperties'
import EventsStore from '../../stores/EventStore/Events.store'
import ProjectDateStore from '../../stores/ui/ProjectDate.store'
import {
  isRunningInNativeApp,
  nativeSupportsOpenPdf,
  openPdfInNativeApp,
} from '../../utils/nativeBridge'
import { NOOP } from '../../utils/noop'
import PdfDocument from '../PdfDocument/PdfDocument'
import PinchZoomPan from '../SitemapsGallery/PinchZoomPan'
import UsernameFromUid from '../UsernameFromUid'
import FileFullscreenPreviewStore, {
  IFullscreenPreviewComment,
  MAX_SCALE,
  MIN_SCALE,
} from './FileFullscreenPreview.store'

import Colors from '~/client/src/shared/theme.module.scss'
import './FileFullscreenPreview.scss'

// White glyphs on the dark preview backdrop.
const OVERLAY_BUTTON_CLASS =
  'tw:text-white tw:hover:bg-white/15 tw:hover:text-white'

const MAX_PERCENT = 100
const CARDS_OFFSET = 12
const MIN_SWIPE_DISTANCE_PX = 50
// Panning is only meaningful once zoomed in; below this the horizontal drag
// is a slide-change gesture.
const SWIPE_MAX_SCALE = 1.01

function getEventPoint(event: TouchEvent | MouseEvent): {
  x: number
  y: number
} {
  if ('touches' in event) {
    const touch = event.touches[0] || event.changedTouches[0]
    return { x: touch.clientX, y: touch.clientY }
  }
  return { x: event.clientX, y: event.clientY }
}
const PDF_WIDTH_MARGIN = 20
const PDF_HEIGHT_MARGIN = 70

interface IProps {
  files: IFilePreviewProperties[]
  fileIndex: number
  onClose: () => void
  comments?: IFullscreenPreviewComment[]
  setFileIndex?: (index: number) => void
  isMobile?: boolean
  className?: string
  showToolbars?: boolean

  eventsStore?: EventsStore
  projectDateStore?: ProjectDateStore
}

const transformCSSKey = 'transform'
const TRANSFORM_COMPONENT_STYLE: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}
const pageScrollOptions: ScrollIntoViewOptions = {
  block: 'start',
  inline: 'start',
}

@inject('eventsStore', 'projectDateStore')
@observer
export default class FileFullscreenPreview extends React.Component<IProps> {
  @observable private ignoreNextScroll: boolean = false
  private imageRef: HTMLDivElement = null
  @observable private isDragging: boolean = false
  @observable private lastX: number = 0
  @observable private lastY: number = 0
  @observable private dX: number = 0
  @observable private dY: number = 0
  private pdfPageRefs: HTMLDivElement[] = []
  @observable private swipeableImageIndex: number = null
  private store: FileFullscreenPreviewStore = null
  private fullscreenPreviewRef: HTMLDivElement = null
  private lastHandedOffUrl: string = null

  public constructor(props: IProps) {
    super(props)

    this.store = new FileFullscreenPreviewStore(props.eventsStore)

    if (props.isMobile) {
      this.swipeableImageIndex = props.fileIndex
    }

    makeObservable(this)
  }

  private get currentFile(): IFilePreviewProperties {
    return this.props.files[this.swipeableImageIndex ?? this.props.fileIndex]
  }

  private get currentComment(): IFullscreenPreviewComment {
    return this.props.comments?.[this.props.fileIndex]
  }

  public componentDidMount() {
    this.fullscreenPreviewRef?.focus()

    // Large PDFs OOM the iOS WebView when rendered inline with pdf.js
    // (STX1-603). In the native app — and on mobile — hand the file to the OS's
    // native PDF viewer instead of fetching the whole blob into memory, and
    // skip the blob fetch entirely. See renderPDFPreview.
    if (this.shouldHandOffPdfToOs) {
      this.maybeHandOffPdf()
      return
    }

    this.store.updateFileUrls(this.currentFile.fileUrl)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    // The active file can change via props (files/fileIndex) or the local swipe
    // observable (swipeableImageIndex) without a prop change. maybeHandOffPdf()
    // is idempotent per file URL, so handle both paths.
    if (this.shouldHandOffPdfToOs) {
      this.maybeHandOffPdf()
      return
    }

    if (
      prevProps.files !== this.props.files ||
      prevProps.fileIndex !== this.props.fileIndex
    ) {
      this.store.updateFileUrls(this.currentFile.fileUrl)
    }
  }

  // Hand PDFs to the OS viewer (never pdf.js) when the native shell handles
  // OPEN-PDF, or on a mobile web browser. Older native builds fall back to
  // pdf.js so they aren't left with a dismissed, empty preview (STX1-603).
  private get shouldHandOffPdfToOs(): boolean {
    if (this.currentFile?.fileType !== FileType.Pdf) return false
    return (
      nativeSupportsOpenPdf() ||
      (!!this.props.isMobile && !isRunningInNativeApp())
    )
  }

  // Fire the OS handoff once per file: the active file can change without a
  // prop change (swipe), and re-renders must not re-trigger it.
  private maybeHandOffPdf(): void {
    const { fileUrl } = this.currentFile
    if (this.lastHandedOffUrl === fileUrl) return
    this.lastHandedOffUrl = fileUrl
    this.handOffPdfToOs()
  }

  private handOffPdfToOs(): void {
    const { fileUrl, fileName } = this.currentFile
    // Native app: hand to the OS PDF viewer and dismiss this overlay.
    // Mobile web: handled by the "Open PDF" affordance in renderPDFPreview,
    // which opens a new tab on tap (preserves the user gesture / avoids popup
    // blockers from auto-opening here).
    if (openPdfInNativeApp(fileUrl, fileName)) {
      this.props.onClose()
    }
  }

  public render(): JSX.Element {
    const { className } = this.props
    return (
      <div
        className={classList({
          'file-fullscreen-preview-container z-index-120 full-width full-height fixed': true,
          [className]: !!className,
        })}
        onClick={this.stopPropagation}
        ref={ref => {
          this.fullscreenPreviewRef = ref
        }}
        tabIndex={0}
        onKeyDown={this.handleKeyDown}
      >
        <div className="file-fullscreen-preview col">
          {this.renderToolbar()}
          {this.renderPreview()}
          <div className="bottom-panel-holder full-width">
            {this.currentComment && this.renderComments()}
          </div>
        </div>
      </div>
    )
  }

  private onClose = (
    event: React.MouseEvent | React.KeyboardEvent<HTMLDivElement>,
  ): void => {
    event.preventDefault()
    event.stopPropagation()
    this.props.onClose()
  }

  private renderToolbar(): JSX.Element {
    const { isMobile } = this.props
    const { fileType, fileName, fileUrl } = this.currentFile
    const { blobUrl, pagesLabel } = this.store
    const isPDF = fileType === FileType.Pdf

    return (
      <div className="tw:absolute tw:z-1000 tw:grid tw:w-full tw:grid-cols-[minmax(max-content,1fr)_minmax(0,auto)_minmax(max-content,1fr)] tw:items-center tw:overflow-hidden tw:bg-linear-to-b tw:from-black/35 tw:to-transparent tw:p-1.25">
        <div className="tw:flex tw:items-center">
          {isPDF && !this.shouldHandOffPdfToOs && (
            <div className="tw:mx-2.5 tw:flex tw:items-center">
              {!isMobile && (
                <Button
                  variant="ghost"
                  size="icon-lg"
                  className={OVERLAY_BUTTON_CLASS}
                  aria-label={Localization.translator.previous}
                  onClick={this.openPrevPage}
                >
                  <ChevronLeftIcon className="tw:size-6" />
                </Button>
              )}
              <span className="tw:text-body-md tw:text-white">
                {pagesLabel}
              </span>
              {!isMobile && (
                <Button
                  variant="ghost"
                  size="icon-lg"
                  className={OVERLAY_BUTTON_CLASS}
                  aria-label={Localization.translator.next}
                  onClick={this.openNextPage}
                >
                  <ChevronRightIcon className="tw:size-6" />
                </Button>
              )}
            </div>
          )}
          <Button
            variant="ghost"
            size="icon-lg"
            className={`tw:flex-none! ${OVERLAY_BUTTON_CLASS}`}
            aria-label={Localization.translator.download}
            nativeButton={false}
            render={
              <a
                href={blobUrl || fileUrl}
                target="_blank"
                download={fileName}
              />
            }
          >
            <DownloadIcon className="tw:size-6" />
          </Button>
          {!isPDF && (
            <Button
              variant="ghost"
              size="icon-lg"
              className={`tw:flex-none! ${OVERLAY_BUTTON_CLASS}`}
              aria-label={Localization.translator.print}
              onClick={this.printImage}
            >
              <PrinterIcon className="tw:size-6" />
            </Button>
          )}
        </div>
        <div className="tw:flex tw:min-w-0 tw:items-center tw:justify-center">
          {fileName &&
            (isMobile ? (
              <span className="tw:truncate tw:text-body-md tw:text-white">
                {fileName}
              </span>
            ) : (
              <>
                {this.getToolbarIcon(fileType)}
                <span className="tw:truncate tw:text-body-md tw:text-white">
                  {fileName}
                </span>
              </>
            ))}
        </div>
        <Button
          variant="ghost"
          size="icon-lg"
          className={`tw:justify-self-end ${OVERLAY_BUTTON_CLASS}`}
          aria-label={Localization.translator.close}
          onClick={this.onClose}
        >
          <XIcon className="tw:size-6" />
        </Button>
      </div>
    )
  }

  @action.bound
  private openNextPage(event: React.MouseEvent) {
    this.store.openNextPage(event)
    const ref = this.pdfPageRefs[this.store.pageNumber - 1]
    if (ref) {
      this.ignoreNextScroll = true
      ref.scrollIntoView(pageScrollOptions)
    }
  }

  @action.bound
  private openPrevPage(event: React.MouseEvent) {
    this.store.openPrevPage(event)
    const ref = this.pdfPageRefs[this.store.pageNumber - 1]
    if (ref) {
      this.ignoreNextScroll = true
      ref.scrollIntoView(pageScrollOptions)
    }
  }

  private getToolbarIcon(fileType: FileType): JSX.Element {
    switch (fileType) {
      case FileType.Video:
        return (
          <span
            className="pa10 no-grow pointer tw:inline-flex tw:z-2"
            onClick={this.onClose}
          >
            <VideoIcon size={24} color={Colors.error50} />
          </span>
        )
      case FileType.Pdf:
        return (
          <Icons.Pdf
            className="pdf-icon pa10 no-grow pointer"
            onClick={this.onClose}
          />
        )
      default:
        return (
          <Icons.Photo
            className="image-video-icon pa10 no-grow pointer"
            onClick={this.onClose}
          />
        )
    }
  }

  private renderPreview(): JSX.Element {
    const { isMobile } = this.props
    const { fileType } = this.currentFile

    switch (fileType) {
      case FileType.Image:
      case FileType.Video:
        if (isMobile) {
          return this.renderSwipeableImagesVideos(fileType === FileType.Video)
        }

        return this.renderImageVideoPreview(fileType === FileType.Video)
      case FileType.Pdf:
        return this.renderPDFPreview()

      default:
        throw new Error('Cannot preview file: Unhandled file type')
    }
  }

  private renderPDFPreview(): JSX.Element {
    // Native app or mobile: never render pdf.js — large PDFs load the whole file
    // into the WebView's WebContent process and OOM-jetsam it (STX1-603). Hand
    // off to the OS viewer / new tab instead. Desktop keeps inline pdf.js below.
    if (this.shouldHandOffPdfToOs) {
      return this.renderMobilePdfHandoff()
    }

    const {
      onDocumentLoadSuccess,
      pageNumber,
      url,
      scale,
      scaleIn,
      scaleOut,
      shouldFitToWidth,
    } = this.store

    return (
      <>
        <div
          onMouseDown={this.handleMouseDown}
          onMouseMove={this.handleMouseMove.bind(this, false)}
          onMouseUp={this.handleMouseUp}
          className={classList({
            'full-height full-width overflow-auto': true,
            'grabbing-cursor': this.isDragging,
            'grab-cursor': !this.isDragging,
          })}
        >
          <PinchZoomPan
            maxScale={MAX_SCALE}
            minScale={MIN_SCALE}
            position="center"
            doubleTapBehavior="zoom"
            onScaleChanged={NOOP}
            initialScale={scale}
            zoomButtons={false}
          >
            <div
              onScroll={this.handleScroll}
              className="zoom-box-pdf-holder scrollable"
            >
              <PdfDocument
                file={url}
                widthMargin={PDF_WIDTH_MARGIN}
                heightMargin={PDF_HEIGHT_MARGIN}
                shouldFitToWidth={shouldFitToWidth}
                pageNumber={pageNumber}
                shouldRenderAllPages={true}
                loaderSize="lg"
                scale={scale}
                onLoadSuccess={onDocumentLoadSuccess}
                setPageRef={this.setPageRefs}
              />
            </div>
          </PinchZoomPan>
        </div>

        {this.renderZoomBar(
          scaleIn,
          this.resetPdfPinchPan,
          shouldFitToWidth ? (
            <ScanIcon className="tw:size-5" />
          ) : (
            <MoveHorizontalIcon className="tw:size-5" />
          ),
          scaleOut,
        )}
      </>
    )
  }

  private renderZoomBar(
    onZoomIn: () => void,
    onReset: () => void,
    resetIcon: JSX.Element,
    onZoomOut: () => void,
  ): JSX.Element {
    return (
      <div className="tw:absolute tw:bottom-2.5 tw:flex tw:w-full tw:justify-center tw:select-none">
        <div className="tw:flex tw:items-center tw:gap-1 tw:rounded-xl tw:bg-black/70 tw:p-1">
          <Button
            variant="ghost"
            size="icon-lg"
            className={OVERLAY_BUTTON_CLASS}
            aria-label={Localization.translator.zoomIn}
            onClick={onZoomIn}
          >
            <ZoomInIcon className="tw:size-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon-lg"
            className={OVERLAY_BUTTON_CLASS}
            aria-label={Localization.translator.reset}
            onClick={onReset}
          >
            {resetIcon}
          </Button>
          <Button
            variant="ghost"
            size="icon-lg"
            className={OVERLAY_BUTTON_CLASS}
            aria-label={Localization.translator.zoomOut}
            onClick={onZoomOut}
          >
            <ZoomOutIcon className="tw:size-5" />
          </Button>
        </div>
      </div>
    )
  }

  private renderMobilePdfHandoff(): JSX.Element {
    const { fileUrl, fileName } = this.currentFile
    const label = fileName || 'Open PDF'

    // Native: tappable fallback (auto-handoff usually dismisses first). Must
    // call the bridge — anchors/new windows are no-ops inside the WebView.
    if (nativeSupportsOpenPdf()) {
      return (
        <div
          className="full-height full-width col x-center y-center pointer"
          onClick={() => this.handOffPdfToOs()}
        >
          <div className="row no-grow x-center y-center text white large pa20">
            <Icons.Pdf className="pdf-icon mr10 no-grow" />
            {label}
          </div>
        </div>
      )
    }

    // Mobile web: open in a new tab → browser's native, streamed PDF viewer.
    return (
      <div className="full-height full-width col x-center y-center">
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="row no-grow x-center y-center text white large pa20 pointer"
        >
          <Icons.Pdf className="pdf-icon mr10 no-grow" />
          {label}
        </a>
      </div>
    )
  }

  @action.bound
  private handleScroll(
    event: React.UIEvent,
    contentComponent?: HTMLDivElement,
  ): void {
    if (this.ignoreNextScroll) {
      this.ignoreNextScroll = false
      return
    }

    const offsets = this.pdfPageRefs.map((ref, index) => {
      if (!ref) return Infinity
      const rect = ref.getBoundingClientRect()
      let containerTop = 0
      if (contentComponent) {
        containerTop = -contentComponent.getBoundingClientRect().top
        return Math.abs(rect.height * index - containerTop)
      }

      containerTop = event.currentTarget.getBoundingClientRect().top
      return Math.abs(rect.top - containerTop)
    })

    const closestPageIndex = offsets.indexOf(Math.min(...offsets))
    if (closestPageIndex !== -1) {
      this.store.pageNumber = closestPageIndex + 1
    }
  }

  private renderImageVideoPreview(isVideo: boolean): JSX.Element {
    const { url } = this.store
    return (
      <div
        key={url}
        className="full-height full-width no-flex-children row x-center y-center"
      >
        {this.shouldRenderNavigationArrows && this.renderNavigationArrows()}
        <TransformWrapper
          minScale={MIN_SCALE}
          maxScale={MAX_SCALE}
          centerOnInit={true}
          initialScale={1}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <TransformComponent
                wrapperStyle={TRANSFORM_COMPONENT_STYLE}
                contentStyle={TRANSFORM_COMPONENT_STYLE}
              >
                <div
                  ref={ref => (this.imageRef = ref)}
                  onMouseDown={this.handleMouseDown}
                  onMouseMove={this.handleMouseMove.bind(this, true)}
                  onMouseUp={this.handleMouseUp}
                  className="full-width full-height row x-center y-center"
                >
                  {isVideo ? (
                    <video
                      className="img-viewer no-select object-fit-contain"
                      src={url}
                      controls
                      tabIndex={0}
                    />
                  ) : (
                    <img
                      className="img-viewer no-select object-fit-contain"
                      src={url}
                    />
                  )}
                </div>
              </TransformComponent>
              {this.renderZoomBar(
                () => zoomIn(),
                this.resetImageTransform.bind(null, resetTransform),
                <ScanIcon className="tw:size-5" />,
                () => zoomOut(),
              )}
            </>
          )}
        </TransformWrapper>
      </div>
    )
  }

  @action.bound
  private resetImageTransform(resetTransform: () => void): void {
    resetTransform()
    this.isDragging = false
    this.dX = 0
    this.dY = 0
    this.imageRef.style.removeProperty(transformCSSKey)
  }

  private get shouldRenderNavigationArrows(): boolean {
    return this.props.setFileIndex && this.props.files.length > 1
  }

  private stopPropagation = (event: React.MouseEvent): void => {
    event.stopPropagation()
  }

  @action.bound
  private goToPreviousFile(): void {
    const { files, fileIndex, setFileIndex } = this.props
    if (fileIndex) {
      setFileIndex(fileIndex - 1)
    } else {
      setFileIndex(files.length - 1)
    }
  }

  @action.bound
  private goToNextFile(): void {
    const { files, fileIndex, setFileIndex } = this.props
    if (fileIndex === files.length - 1) {
      setFileIndex(0)
    } else {
      setFileIndex(fileIndex + 1)
    }
  }

  @action.bound
  private swipeRight(): void {
    const { files } = this.props
    if (this.swipeableImageIndex) {
      this.swipeableImageIndex--
    } else {
      this.swipeableImageIndex = files.length - 1
    }
  }

  // The zoom wrapper stops propagation of every touch it receives, so the
  // SwipeableWrapper above it never sees gestures that begin on the image.
  // These callbacks reconstruct the swipe from the wrapper's own panning
  // events: an unzoomed, mostly-horizontal drag changes the slide.
  private swipeGestureStart: { x: number; y: number } = null

  private handleImagePanningStart = (
    _ref: ReactZoomPanPinchRef,
    event: TouchEvent | MouseEvent,
  ): void => {
    if ('touches' in event && event.touches.length > 1) {
      this.swipeGestureStart = null
      return
    }
    this.swipeGestureStart = getEventPoint(event)
  }

  private handleImagePanningStop = (
    ref: ReactZoomPanPinchRef,
    event: TouchEvent | MouseEvent,
  ): void => {
    const start = this.swipeGestureStart
    this.swipeGestureStart = null

    const isPinchStillActive = 'touches' in event && event.touches.length > 0
    if (!start || isPinchStillActive || ref.state.scale > SWIPE_MAX_SCALE) {
      return
    }

    const end = getEventPoint(event)
    const deltaX = end.x - start.x
    const deltaY = end.y - start.y
    if (
      Math.abs(deltaX) < MIN_SWIPE_DISTANCE_PX ||
      Math.abs(deltaX) < Math.abs(deltaY)
    ) {
      return
    }

    if (deltaX < 0) {
      this.swipeLeft()
    } else {
      this.swipeRight()
    }
  }

  private setPageRefs = (ref: HTMLDivElement, index: number): void => {
    this.pdfPageRefs[index] = ref
  }

  @action.bound
  private swipeLeft(): void {
    const { files } = this.props
    if (this.swipeableImageIndex === files.length - 1) {
      this.swipeableImageIndex = 0
    } else {
      this.swipeableImageIndex++
    }
  }

  private renderSwipeableImagesVideos(isVideo: boolean): JSX.Element {
    const { files } = this.props

    const percentTranslate = MAX_PERCENT * this.swipeableImageIndex
    const pixelTranslate = this.swipeableImageIndex * CARDS_OFFSET

    return (
      <SwipeableWrapper
        onSwipedRight={this.swipeRight}
        onSwipedLeft={this.swipeLeft}
        trackMouse={true}
        className="full-width full-height overflow-hidden"
      >
        <div
          className="swipeable-image-container nowrap py10 full-height"
          style={{
            transform: `translateX(calc(-${percentTranslate}% - ${pixelTranslate}px))`,
          }}
        >
          {files.map((file, index) => {
            return (
              <div
                key={index}
                className="swipeable-image inline-block mr12 full-height row x-center y-center full-height"
              >
                <TransformWrapper
                  initialScale={1}
                  minScale={MIN_SCALE}
                  maxScale={MAX_SCALE}
                  centerOnInit={true}
                  doubleClick={{
                    mode: 'reset',
                  }}
                  onPanningStart={this.handleImagePanningStart}
                  onPanningStop={this.handleImagePanningStop}
                >
                  <TransformComponent
                    wrapperStyle={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    contentStyle={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {isVideo ? (
                      <video
                        className="img-viewer swipeable no-select full-height full-width object-fit-contain"
                        src={file.fileUrl}
                        controls
                      />
                    ) : (
                      <img
                        className="img-viewer swipeable no-select full-height full-width object-fit-contain"
                        src={file.fileUrl}
                        draggable={false}
                      />
                    )}
                  </TransformComponent>
                </TransformWrapper>
              </div>
            )
          })}
        </div>
      </SwipeableWrapper>
    )
  }

  private renderNavigationArrows(): JSX.Element {
    return (
      <>
        <div className="navigation-icon-wrapper-left full-height col absolute">
          <Button
            variant="ghost"
            size="icon-lg"
            className={`navigation-icon tw:ml-5 ${OVERLAY_BUTTON_CLASS}`}
            aria-label={Localization.translator.previous}
            onClick={this.goToPreviousFile}
          >
            <ChevronLeftIcon />
          </Button>
        </div>
        <div className="navigation-icon-wrapper-right full-height col absolute">
          <Button
            variant="ghost"
            size="icon-lg"
            className={`navigation-icon tw:mr-5 ${OVERLAY_BUTTON_CLASS}`}
            aria-label={Localization.translator.next}
            onClick={this.goToNextFile}
          >
            <ChevronRightIcon />
          </Button>
        </div>
      </>
    )
  }

  private renderComments(): JSX.Element {
    const {
      timestamp,
      companyName,
      deliveryStatus,
      authorId,
      text,
      photoDetails,
    } = this.currentComment
    const { getMonthDayAndTimeToDisplay } = this.props.projectDateStore

    return (
      <div className="row bg-white comment-holder ma-x-auto pa15">
        <div className="col y-between full-height small-col">
          <div className="text header bold">
            <div>{companyName}</div>
            <div>{getMonthDayAndTimeToDisplay(timestamp)}</div>
            <div className="row w-fit-content">
              {photoDetails.map((photoDetail, index) => {
                return (
                  <div
                    key={photoDetail + index}
                    className="no-white-space-wrap pr20"
                  >
                    {photoDetail}
                  </div>
                )
              })}
            </div>
          </div>
          <div className="text header">
            {deliveryStatus && this.getDeliveryStatusText(deliveryStatus)}
          </div>
        </div>
        <div className="col full-height">
          <div className="text header bold">
            <UsernameFromUid userId={authorId} showCompany={true} />
          </div>
          <div className="text header">{text}</div>
        </div>
      </div>
    )
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === Keys.Escape) {
      return this.onClose(e)
    }
    if (!this.shouldRenderNavigationArrows) {
      return
    }
    switch (e.key) {
      case Keys.ArrowLeft:
        this.goToPreviousFile()
        break
      case Keys.ArrowRight:
        this.goToNextFile()
        break
    }
  }

  @action.bound
  private printImage() {
    const { fileUrl } = this.currentFile
    const printFrame = document.createElement('iframe')
    printFrame.setAttribute(
      'style',
      'visibility: hidden; height: 0; width: 0; position: absolute; border: 0',
    )
    printFrame.setAttribute('id', fileUrl)
    const imageElement = document.createElement('img')
    imageElement.setAttribute(
      'style',
      'height: calc(100% - 20px);  width: 100%; position: absolute; object-fit: contain;',
    )
    imageElement.src = fileUrl
    const imageWrapper = document.createElement('div')

    imageWrapper.appendChild(imageElement)
    document.getElementsByTagName('body')[0].appendChild(printFrame)
    printFrame.contentWindow.print()

    const printDocument =
      printFrame.contentWindow?.document || printFrame.contentDocument
    printDocument.body.appendChild(imageWrapper)
  }

  private getDeliveryStatusText(status: DeliveryStatus): JSX.Element {
    let icon: JSX.Element

    switch (status) {
      case DeliveryStatus.IncompleteDone:
      case DeliveryStatus.Done:
        icon = <Icons.DeliveryDone className="small-col" />
        break
      case DeliveryStatus.OnSite:
      case DeliveryStatus.Delivering:
      case DeliveryStatus.OnHold:
        icon = <Icons.DeliveryOnsite className="small-col" />
        break
      case DeliveryStatus.PassedInspection:
        icon = <Icons.DeliveryPassedInspection className="small-col" />
        break
      case DeliveryStatus.Requested:
        icon = <Icons.DeliveryRequested className="small-col" />
        break
      case DeliveryStatus.Scheduled:
        icon = <Icons.DeliveryAccepted className="small-col" />
        break
      case DeliveryStatus.Changed:
        icon = <Icons.DeliveryAccepted className="small-col" />
        break
      case DeliveryStatus.Denied:
      case DeliveryStatus.FailedInspection:
      case DeliveryStatus.Paused:
        icon = <Icons.DeliveryFailedInspection className="small-col" />
        break
      case DeliveryStatus.Canceled:
        icon = <Icons.DeliveryAccepted className="small-col" />
        break
      default:
        return null
    }
    return this.getStatus(icon, formatStatusToDisplay(status))
  }

  private getStatus(icon: JSX.Element, text: string): JSX.Element {
    return (
      <div className="row">
        {icon}
        <div>{text}</div>
      </div>
    )
  }

  @action.bound
  private handleMouseDown(e: React.MouseEvent<HTMLDivElement>): void {
    this.isDragging = true
    this.lastX = e.clientX
    this.lastY = e.clientY
  }

  @action.bound
  private handleMouseMove(
    isImage: boolean,
    e: React.MouseEvent<HTMLDivElement>,
  ): void {
    if (!this.isDragging) return

    this.dX += e.clientX - this.lastX
    this.dY += e.clientY - this.lastY

    this.lastX = e.clientX
    this.lastY = e.clientY
    if (isImage) {
      this.imageRef.style.setProperty(
        transformCSSKey,
        `translate3d(${this.dX}px, ${this.dY}px, 0px)`,
      )
    } else {
      this.pdfPageRefs.forEach(ref => {
        ref.style.setProperty(
          transformCSSKey,
          `translate3d(${this.dX}px, ${this.dY}px, 0px)`,
        )
      })
    }
  }

  @action.bound
  private handleMouseUp(): void {
    this.isDragging = false
  }

  @action.bound
  private resetPdfPinchPan() {
    this.dX = 0
    this.dY = 0
    this.lastX = 0
    this.lastY = 0

    this.pdfPageRefs.forEach(ref => {
      ref.style.removeProperty(transformCSSKey)
    })

    this.store.scaleToFit()
  }
}
